import { FC, useEffect, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { useFormContext } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { BaselineDialogProps, FormType } from "../../common";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";

import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";

import {
  BeefBaseline,
  BeefBreedingStageData,
  BeefGrowingStageData,
  BeefOutput,
  BeefOutputBreeding,
  BeefOutputCalves,
  BeefOutputGrowing,
} from "../../../../models/Baseline/BeefBaseline";
import BeefOutputCowForm from "./BeefOutputCowForm";
import BeefOutputCalvesForm from "./BeefOutputCalvesForm";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { StageType } from "../../../../../../graphql/types";
import { Spacer } from "../../../helpers/Spacer";

interface BeefOutputDialogProps extends BaselineDialogProps {
  formType: FormType;
  stageIndex: number;
  baseline: BeefBaseline;
  stageType: StageType;
  handleCancel: (param?: string | null) => void;
}

const BeefOutputDialog: FC<BeefOutputDialogProps> = ({
  formType = FormType.Add,
  stageIndex = 0,
  baseline,
  stageType,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const formTitle = intl.formatMessage({ id: "SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE" });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;

  const currResetValue = useRef<BeefOutputBreeding | BeefOutputGrowing>();
  const fc = useFormContext<BeefIntervention>();

  const currentStageData = baseline.stages[stageIndex].stageData;

  const [isCowOutputEdit, setIsCowOutputEdit] = useState<boolean>(false);
  const [isCalvesOutputEdit, setIsCalvesOutputEdit] = useState<boolean>(false);

  const isAnyOutputEditing = isCowOutputEdit || isCalvesOutputEdit;

  const cowProperties: (keyof BeefOutput)[] = [
    "cowsToAnotherStage",
    "averageWeightOfCowsLeavingTheStage",
    "cowsSold",
    "pricePerCowSold",
    "numberOfMortalitiesCows",
    "averageWeightAtMortalityCows",
  ];

  const calvesProperties: (keyof BeefOutputCalves)[] = [
    "weanedCalvesToAnotherStage",
    "averageWeightOfCalvesLeavingTheStage",
    "weanedCalvesSold",
    "pricePerWeanedCalfSold",
    "numberOfMortalitiesCalves",
    "averageWeightAtMortalityCalves",
  ];

  const getDialogWidth = () => {
    if (stageType === StageType.Growing) {
      return isCowOutputEdit ? "592px" : "460px";
    }
    return isAnyOutputEditing ? "1168px" : "auto";
  };

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogCancelClick = () => {
    if (currResetValue.current) {
      if (stageType === StageType.Growing) {
        Object.entries(currResetValue.current).forEach(
          ([propertyName, resetValue]) => {
            fc.setValue(`${fieldItemPrefix}.${propertyName}`, resetValue);
          }
        );
      } else {
        const cowStage = `${fieldItemPrefix}.cows`;
        const calvesStage = `${fieldItemPrefix}.calves`;

        const resetValue = currResetValue.current as any;

        cowProperties.forEach((propertyName) => {
          const value = resetValue && resetValue.cows ? resetValue.cows[propertyName] : "";
          fc.setValue(`${cowStage}.${propertyName}`, value);
        });

        calvesProperties.forEach((propertyName) => {
          const value = resetValue && resetValue.calves ? resetValue.calves[propertyName] : "";
          fc.setValue(`${calvesStage}.${propertyName}`, value);
        });
      }
    } else {
      const baselineOutputs =
        stageType === StageType.Growing
          ? (currentStageData as BeefGrowingStageData).output
          : (currentStageData as BeefBreedingStageData).output;
      Object.entries(baselineOutputs).forEach(([propertyName, _]) => {
        fc.setValue(`${fieldItemPrefix}.${propertyName}`, "");
      });
    }

    handleCancel?.();
  };

  const renderGrowingContent = () => (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      <Spacer />
      <BeefOutputCowForm
        isOutputEdit={isCowOutputEdit}
        fieldItemPrefix={fieldItemPrefix}
        outputBaselineData={(currentStageData as BeefGrowingStageData).output}
        setIsOutputEdit={setIsCowOutputEdit}
        formType={formType}
        stageType={stageType}
      />
    </DsmGrid>
  );

  const renderBreedingContent = () => (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
      <DsmGrid className={classes.dsmGridOneColumnIntervention}>
        <span className={classes.headingTitleVariant}>
          {intl.formatMessage({
            id: "SUSTELL.STAGE.BEEF.OUTPUT.COWS",
          })}
        </span>
        <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
          <Spacer />
          <BeefOutputCowForm
            isOutputEdit={isCowOutputEdit}
            fieldItemPrefix={`${fieldItemPrefix}.cows`}
            outputBaselineData={
              (currentStageData as BeefGrowingStageData).output
            }
            setIsOutputEdit={setIsCowOutputEdit}
            formType={formType}
            stageType={stageType}
          />
        </DsmGrid>
      </DsmGrid>
      <DsmGrid className={classes.dsmGridOneColumnIntervention}>
        <span className={classes.headingTitleVariant}>
          {intl.formatMessage({
            id: "SUSTELL.STAGE.BEEF.OUTPUT.CALVES",
          })}
        </span>
        <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
          <Spacer />
          <BeefOutputCalvesForm
            isOutputEdit={isCalvesOutputEdit}
            fieldItemPrefix={`${fieldItemPrefix}.calves`}
            outputBaselineData={
              (currentStageData as BeefBreedingStageData).output
            }
            setIsOutputEdit={setIsCalvesOutputEdit}
            formType={formType}
          />
        </DsmGrid>
      </DsmGrid>
    </DsmGrid>
  );

  return (
    <DialogContainer
      formVisible
      handleClose={handleDialogCancelClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      datasetType="intervention"
      width={getDialogWidth()}
    >
      {stageType === StageType.Growing && renderGrowingContent()}
      {stageType === StageType.Breeding && renderBreedingContent()}

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  );
};

export default BeefOutputDialog;
